import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Stack, TextField, MenuItem, CircularProgress, ToggleButton, ToggleButtonGroup, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { useSalonContextStore } from '../../store/salonContextStore';
import { attendanceApi, absenceApi, employeeApi, AttendanceSession, Absence } from '../../services/employeeService';
import { SessionCorrectionDialog } from '../../components/SessionCorrectionDialog';
import {
  PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PersonAvatar, PillTone,
} from '../../components/ui';
import { tokens } from '../../theme';
import {
  countLabel, formatDate, formatDateLong, formatDuration, formatMonthYear, formatNumber, formatTime,
} from '../../utils/format';

type ViewMode = 'month' | 'list';

interface StaffRow { id: string; firstName: string; lastName: string; }

const WEEKDAYS = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'];

const SESSION_STATUS: Record<string, { label: string; tone: PillTone }> = {
  IN_PROGRESS: { label: 'W pracy', tone: 'success' },
  ON_BREAK: { label: 'Wyjście', tone: 'warning' },
  COMPLETED: { label: 'Zakończona', tone: 'neutral' },
  NEEDS_REVIEW: { label: 'Do korekty', tone: 'danger' },
};

const ABSENCE_SHORT: Record<string, string> = {
  SICK_LEAVE: 'L4', VACATION: 'Urlop', EXCUSED: 'Uspr.', UNEXCUSED: 'Nieuspr.', OTHER: 'Inne',
};
const ABSENCE_LABEL: Record<string, string> = {
  SICK_LEAVE: 'L4 (zwolnienie lekarskie)', VACATION: 'Urlop', EXCUSED: 'Nieobecność usprawiedliwiona', UNEXCUSED: 'Nieobecność nieusprawiedliwiona', OTHER: 'Inna nieobecność',
};

const ERR_STAFF = 'Wybierz pracownika.';
const ERR_TIME = 'Uzupełnij datę i godziny.';
const ERR_REASON = 'Podaj uzasadnienie wpisu (min. 3 znaki).';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const pad = (n: number) => String(n).padStart(2, '0');

const LegendItem: React.FC<{ color: string; border?: string; label: string }> = ({ color, border, label }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    <Box sx={{ width: 12, height: 12, borderRadius: '4px', bgcolor: color, border: `1px solid ${border || color}` }} />
    <Typography sx={{ fontSize: '0.8125rem', color: tokens.inkSoft }}>{label}</Typography>
  </Stack>
);

export const TimeCalendarPage: React.FC = () => {
  const { activeSalonId } = useSalonContextStore();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [view, setView] = useState<ViewMode>('month');
  const [eventFilter, setEventFilter] = useState<'all' | 'review'>('all');
  const [sessions, setSessions] = useState<AttendanceSession[]>([]);
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [loading, setLoading] = useState(false);
  const [dayDetail, setDayDetail] = useState<string | null>(null);
  const [editSession, setEditSession] = useState<AttendanceSession | null>(null);

  // Ręczne dodawanie wpisu czasu pracy (właściciel/manager)
  const [staffList, setStaffList] = useState<StaffRow[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ staffId: '', date: '', start: '09:00', end: '17:00', reason: '' });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  useEffect(() => {
    if (!activeSalonId) { setStaffList([]); return; }
    employeeApi.list(activeSalonId).then((rows: StaffRow[]) => setStaffList(rows)).catch(() => setStaffList([]));
  }, [activeSalonId]);

  const openAdd = (date?: string) => {
    setAddForm((f) => ({
      ...f,
      date: date || new Date().toISOString().slice(0, 10),
      staffId: f.staffId || staffList[0]?.id || '',
      reason: '',
    }));
    setAddError('');
    setAddOpen(true);
  };

  const [y, m] = month.split('-').map(Number);

  const load = useCallback(async () => {
    if (!activeSalonId) return;
    setLoading(true);
    const from = `${y}-${String(m).padStart(2, '0')}-01`;
    const to = `${y}-${String(m).padStart(2, '0')}-31`;
    try {
      const [s, a] = await Promise.all([
        attendanceApi.listSessions({ salonId: activeSalonId, from, to, ...(eventFilter === 'review' ? { needsReview: true } : {}) }),
        absenceApi.list({ salonId: activeSalonId, from, to }),
      ]);
      setSessions(s); setAbsences(a);
    } finally { setLoading(false); }
  }, [activeSalonId, y, m, eventFilter]);
  useEffect(() => { load(); }, [load]);

  const shiftMonth = (dir: -1 | 1) => {
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
  };

  const daysInMonth = new Date(y, m, 0).getDate();
  const firstWeekday = (new Date(y, m - 1, 1).getDay() + 6) % 7; // Mon=0

  const byDay = useMemo(() => {
    const map: Record<string, { actual: number; billable: number; breaks: number; review: boolean; absences: Absence[] }> = {};
    sessions.forEach((s) => {
      const d = s.workDate.slice(0, 10);
      map[d] = map[d] || { actual: 0, billable: 0, breaks: 0, review: false, absences: [] };
      map[d].actual += s.actualWorkMinutes;
      map[d].billable += s.billableWorkMinutes;
      map[d].breaks += s.actualBreakMinutes;
      map[d].review = map[d].review || s.needsReview;
    });
    absences.forEach((a) => {
      const start = new Date(a.startDate), end = new Date(a.endDate);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().slice(0, 10);
        if (key.startsWith(`${y}-${String(m).padStart(2, '0')}`)) {
          map[key] = map[key] || { actual: 0, billable: 0, breaks: 0, review: false, absences: [] };
          map[key].absences.push(a);
        }
      }
    });
    return map;
  }, [sessions, absences, y, m]);

  const dayKey = (day: number) => `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const todayKey = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

  const submitAdd = async () => {
    if (!addForm.staffId) { setAddError(ERR_STAFF); return; }
    if (!addForm.date || !addForm.start || !addForm.end) { setAddError(ERR_TIME); return; }
    if (addForm.reason.trim().length < 3) { setAddError(ERR_REASON); return; }
    setAddSaving(true);
    setAddError('');
    try {
      // toISOString: godziny wpisane lokalnie muszą trafić do API jako konkretny moment w czasie
      await attendanceApi.createSession({
        staffId: addForm.staffId,
        workDate: addForm.date,
        startedAt: new Date(`${addForm.date}T${addForm.start}:00`).toISOString(),
        endedAt: new Date(`${addForm.date}T${addForm.end}:00`).toISOString(),
        reason: addForm.reason.trim(),
      });
      setAddOpen(false);
      load();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string; errors?: { msg?: string }[] } } }).response?.data;
      setAddError(msg?.error || msg?.errors?.[0]?.msg || 'Nie udało się dodać wpisu. Sprawdź godziny i spróbuj ponownie.');
    } finally {
      setAddSaving(false);
    }
  };

  const reviewCount = sessions.filter((s) => s.needsReview).length;
  const monthLabel = capitalize(formatMonthYear(new Date(y, m - 1, 1)));
  const daySessions = dayDetail ? sessions.filter((s) => s.workDate.slice(0, 10) === dayDetail) : [];
  const dayAbsences = dayDetail ? byDay[dayDetail]?.absences || [] : [];

  const statusOf = (s: AttendanceSession) => (s.needsReview
    ? SESSION_STATUS.NEEDS_REVIEW
    : SESSION_STATUS[s.status] || { label: s.status, tone: 'neutral' as PillTone });

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Zespół' }]}
        title="Kalendarz pracy"
        description={loading
          ? `${monthLabel} · kliknij dzień, aby zobaczyć zmiany albo dodać wpis`
          : `${monthLabel} · ${countLabel(sessions.length, 'zmiana', 'zmiany', 'zmian')}${reviewCount ? ` · ${countLabel(reviewCount, 'wymaga', 'wymagają', 'wymaga')} korekty` : ''}`}
        actions={(
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAdd()}>
            Dodaj wpis
          </Button>
        )}
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)} aria-label="Widok">
            <ToggleButton value="month">Miesiąc</ToggleButton>
            <ToggleButton value="list">Lista</ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Tooltip title="Poprzedni miesiąc">
              <IconButton onClick={() => shiftMonth(-1)} aria-label="Poprzedni miesiąc"><ChevronLeftIcon /></IconButton>
            </Tooltip>
            <TextField type="month" size="small" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} inputProps={{ 'aria-label': 'Miesiąc' }} sx={{ width: 190 }} />
            <Tooltip title="Następny miesiąc">
              <IconButton onClick={() => shiftMonth(1)} aria-label="Następny miesiąc"><ChevronRightIcon /></IconButton>
            </Tooltip>
          </Stack>
          <TextField
            select
            size="small"
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value as 'all' | 'review')}
            inputProps={{ 'aria-label': 'Zdarzenia' }}
            sx={{ minWidth: 210 }}
          >
            <MenuItem value="all">Wszystkie zmiany</MenuItem>
            <MenuItem value="review">Tylko wymagające korekty</MenuItem>
          </TextField>
        </Stack>
      </PageHeader>

      {loading ? (
        <LoadingState variant={view === 'month' ? 'block' : 'table'} height={420} rows={6} />
      ) : view === 'month' ? (
        <SectionCard>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.5, md: 0.75 } }}>
            {WEEKDAYS.map((d) => (
              <Typography key={d} sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, textAlign: 'center', pb: 0.5 }}>{d}</Typography>
            ))}
            {Array.from({ length: firstWeekday }).map((_, i) => <Box key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const key = dayKey(day);
              const info = byDay[key];
              const isToday = key === todayKey;
              const open = () => (info ? setDayDetail(key) : openAdd(key));
              const bg = info?.review ? tokens.dangerSoft : info?.absences.length ? tokens.warningSoft : info ? tokens.plumSoft : tokens.paper;
              const border = info?.review ? alpha(tokens.danger, 0.25) : info?.absences.length ? alpha(tokens.warning, 0.3) : info ? alpha(tokens.plum, 0.22) : tokens.line;
              return (
                <Box
                  key={day}
                  role="button"
                  tabIndex={0}
                  aria-label={`${formatDateLong(`${key}T00:00:00`)}${info ? '' : ' — dodaj wpis'}`}
                  onClick={open}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                  sx={{
                    minWidth: 0,
                    minHeight: { xs: 58, sm: 84, lg: 96 },
                    p: { xs: 0.5, sm: 1 },
                    border: `1px solid ${border}`,
                    borderRadius: { xs: '8px', sm: '10px' },
                    bgcolor: bg,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    overflow: 'hidden',
                    transition: 'border-color .15s ease, box-shadow .15s ease',
                    '&:hover': { borderColor: tokens.plum, boxShadow: `0 0 0 1px ${tokens.plum}` },
                    '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      alignSelf: 'flex-start',
                      minWidth: 22,
                      height: 22,
                      px: 0.5,
                      borderRadius: 999,
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: '0.8125rem',
                      fontWeight: isToday ? 700 : 600,
                      fontVariantNumeric: 'tabular-nums',
                      color: isToday ? tokens.paper : info ? tokens.ink : tokens.muted,
                      bgcolor: isToday ? tokens.plum : 'transparent',
                    }}
                  >
                    {day}
                  </Box>
                  {info && info.actual > 0 && (
                    <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' }, fontWeight: 650, color: tokens.plum, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{formatNumber(info.billable / 60, 1)} h</Box>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{formatDuration(info.billable)}</Box>
                    </Typography>
                  )}
                  {info && (info.absences.length > 0 || info.review) ? (
                    <>
                      <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                        {info.absences.map((a) => (
                          <StatusPill key={a.id} size="small" tone="warning" noDot label={ABSENCE_SHORT[a.type] || 'Nieobecność'} />
                        ))}
                        {info.review && <StatusPill size="small" tone="danger" label="Do korekty" />}
                      </Stack>
                      <Stack direction="row" gap={0.5} sx={{ display: { xs: 'flex', sm: 'none' }, mt: 'auto' }} aria-hidden>
                        {info.absences.length > 0 && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: tokens.warning }} />}
                        {info.review && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: tokens.danger }} />}
                      </Stack>
                    </>
                  ) : null}
                </Box>
              );
            })}
          </Box>
          <Stack direction="row" gap={{ xs: 1.5, sm: 2.5 }} flexWrap="wrap" sx={{ mt: 2 }}>
            <LegendItem color={tokens.plumSoft} border={alpha(tokens.plum, 0.22)} label="Przepracowany dzień (czas rozliczeniowy)" />
            <LegendItem color={tokens.warningSoft} border={alpha(tokens.warning, 0.3)} label="Nieobecność" />
            <LegendItem color={tokens.dangerSoft} border={alpha(tokens.danger, 0.25)} label="Zmiana do korekty" />
          </Stack>
        </SectionCard>
      ) : sessions.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<EventBusyOutlinedIcon />}
            title={eventFilter === 'review' ? 'Nic nie wymaga korekty' : 'Brak zmian w tym miesiącu'}
            description={eventFilter === 'review'
              ? 'Wszystkie zmiany w tym miesiącu są zamknięte i poprawne.'
              : 'Zmiany pojawią się, gdy pracownicy zarejestrują czas pracy. Brakujący dzień możesz dodać ręcznie.'}
            actions={eventFilter === 'review' ? undefined : <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openAdd()}>Dodaj wpis</Button>}
          />
        </SectionCard>
      ) : (
        <SectionCard title="Zmiany w miesiącu" subtitle={monthLabel} padding="none">
          <TableContainer>
            <Table sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Dzień</TableCell>
                  <TableCell>Pracownik</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>Koniec</TableCell>
                  <TableCell align="right">Czas faktyczny</TableCell>
                  <TableCell align="right">Czas rozliczeniowy</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" aria-label="Akcje" />
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((s) => {
                  const st = statusOf(s);
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDate(`${s.workDate.slice(0, 10)}T00:00:00`)}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1.25}>
                          <PersonAvatar firstName={s.staff?.firstName} lastName={s.staff?.lastName} seed={s.staff?.id} size={32} />
                          <Typography sx={{ fontWeight: 600, fontSize: 'inherit' }}>{s.staff?.firstName} {s.staff?.lastName}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(s.startedAt)}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{s.endedAt ? formatTime(s.endedAt) : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatDuration(s.actualWorkMinutes)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatDuration(s.billableWorkMinutes)}</TableCell>
                      <TableCell><StatusPill label={st.label} tone={st.tone} /></TableCell>
                      <TableCell align="right"><Button size="small" variant="outlined" onClick={() => setEditSession(s)}>Koryguj</Button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      <Dialog open={!!dayDetail} onClose={() => setDayDetail(null)} fullWidth maxWidth="sm">
        <DialogTitle>{dayDetail ? capitalize(formatDateLong(`${dayDetail}T00:00:00`)) : ''}</DialogTitle>
        <DialogContent>
          {daySessions.length === 0 && dayAbsences.length === 0 && (
            <Typography sx={{ color: tokens.inkSoft }}>Tego dnia nikt nie zarejestrował czasu pracy.</Typography>
          )}
          {daySessions.length > 0 && (
            <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line}` }} />}>
              {daySessions.map((s) => {
                const st = statusOf(s);
                return (
                  <Stack key={s.id} direction="row" alignItems="center" gap={1.5} sx={{ py: 1.5 }}>
                    <PersonAvatar firstName={s.staff?.firstName} lastName={s.staff?.lastName} seed={s.staff?.id} size={36} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography sx={{ fontWeight: 650 }}>{s.staff?.firstName} {s.staff?.lastName}</Typography>
                        <StatusPill size="small" label={st.label} tone={st.tone} />
                      </Stack>
                      <Typography variant="body2" sx={{ color: tokens.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
                        {formatTime(s.startedAt)} – {s.endedAt ? formatTime(s.endedAt) : '…'}
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        Faktyczny {formatDuration(s.actualWorkMinutes)} · Rozliczeniowy {formatDuration(s.billableWorkMinutes)} · Wyjścia {formatDuration(s.actualBreakMinutes)}
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => { setDayDetail(null); setEditSession(s); }} sx={{ flexShrink: 0 }}>
                      Koryguj
                    </Button>
                  </Stack>
                );
              })}
            </Stack>
          )}
          {dayAbsences.length > 0 && (
            <Box sx={{ mt: daySessions.length ? 1.5 : 0 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 1 }}>Nieobecności</Typography>
              <Stack gap={1}>
                {dayAbsences.map((a) => (
                  <Stack key={a.id} direction="row" alignItems="center" gap={1.25} sx={{ bgcolor: tokens.warningSoft, borderRadius: '10px', px: 1.5, py: 1 }}>
                    <PersonAvatar firstName={a.staff?.firstName} lastName={a.staff?.lastName} seed={a.staff?.id} size={28} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 0 }}>{a.staff?.firstName} {a.staff?.lastName}</Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: tokens.warningText, ml: 'auto', textAlign: 'right' }}>{ABSENCE_LABEL[a.type] || 'Nieobecność'}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDayDetail(null)}>Zamknij</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { const d = dayDetail; setDayDetail(null); if (d) openAdd(d); }}>
            Dodaj wpis
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ręczny wpis czasu pracy */}
      <Dialog open={addOpen} onClose={() => !addSaving && setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Dodaj wpis czasu pracy</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {addError && ![ERR_STAFF, ERR_TIME, ERR_REASON].includes(addError) && (
              <Alert severity="error" onClose={() => setAddError('')}>{addError}</Alert>
            )}
            <TextField select fullWidth label="Pracownik" value={addForm.staffId}
              error={addError === ERR_STAFF} helperText={addError === ERR_STAFF ? ERR_STAFF : undefined}
              onChange={(e) => setAddForm({ ...addForm, staffId: e.target.value })}>
              {staffList.map((s) => <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</MenuItem>)}
            </TextField>
            <TextField type="date" fullWidth label="Data" InputLabelProps={{ shrink: true }}
              error={addError === ERR_TIME && !addForm.date}
              value={addForm.date} onChange={(e) => setAddForm({ ...addForm, date: e.target.value })} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
              <TextField type="time" fullWidth label="Rozpoczęcie" InputLabelProps={{ shrink: true }}
                error={addError === ERR_TIME && !addForm.start}
                value={addForm.start} onChange={(e) => setAddForm({ ...addForm, start: e.target.value })} />
              <TextField type="time" fullWidth label="Zakończenie" InputLabelProps={{ shrink: true }}
                error={addError === ERR_TIME && !addForm.end}
                value={addForm.end} onChange={(e) => setAddForm({ ...addForm, end: e.target.value })} />
            </Box>
            {addError === ERR_TIME && <Typography sx={{ color: tokens.danger, fontSize: '0.8125rem', fontWeight: 600, mt: '6px !important' }}>{ERR_TIME}</Typography>}
            <TextField fullWidth label="Uzasadnienie" required multiline rows={2}
              placeholder="np. ewidencja wsteczna — pracownik zapomniał się zalogować"
              error={addError === ERR_REASON}
              helperText={addError === ERR_REASON ? ERR_REASON : 'Trafi do dziennika zmian razem z wpisem.'}
              value={addForm.reason} onChange={(e) => setAddForm({ ...addForm, reason: e.target.value })} />
            <Typography variant="body2" sx={{ color: tokens.muted }}>
              Wpis zostanie oznaczony jako ręczny. Czas rozliczeniowy przeliczy system według ustawień rozliczania salonu.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setAddOpen(false)} disabled={addSaving}>Anuluj</Button>
          <Button variant="contained" onClick={submitAdd} disabled={addSaving}
            startIcon={addSaving ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {addSaving ? 'Zapisywanie…' : 'Dodaj wpis'}
          </Button>
        </DialogActions>
      </Dialog>

      <SessionCorrectionDialog session={editSession} onClose={() => setEditSession(null)} onSaved={load} />
    </Box>
  );
};

export default TimeCalendarPage;
